import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bookmark, BookmarkCheck, Clock, MapPin, Briefcase, Zap, AlertTriangle, ExternalLink, Loader2, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SavedRolesTab = ({ authHeaders }) => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [removing, setRemoving] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/users/me/vacancy-bookmarks`, { headers: authHeaders() });
      setItems(r.data.bookmarks || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { load(); }, [load]);

  const unsave = async (vacancy_id) => {
    setRemoving(vacancy_id);
    try {
      await axios.post(`${BACKEND_URL}/api/users/me/vacancy-bookmarks/${vacancy_id}`, {}, { headers: authHeaders() });
      setItems(prev => prev.filter(v => v.id !== vacancy_id));
      toast.success('Removed from saved roles');
    } catch {
      toast.error('Could not remove');
    } finally {
      setRemoving(null);
    }
  };

  return (
    <div data-testid="saved-roles-panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-white text-xl font-medium">Saved Roles</h2>
          <p className="text-white/40 text-xs mt-0.5">Vacancies you've bookmarked — we'll nudge you before they close.</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={load} variant="ghost" data-testid="refresh-saved-btn" className="text-white/50 hover:text-white rounded-xl">
            <RefreshCw className={`w-3.5 h-3.5 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Button onClick={() => navigate('/vacancies')} data-testid="saved-browse-btn"
            className="bg-gradient-to-r from-[#00E5FF] to-[#8A2BE2] text-black hover:opacity-90 rounded-xl">
            <Briefcase className="w-3.5 h-3.5 mr-2" /> Browse jobs
          </Button>
        </div>
      </div>
      {loading && items.length === 0 ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-white/40 animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="bg-white/[0.02] border border-white/[0.06] rounded-2xl p-16 text-center" data-testid="no-saved-roles">
          <div className="w-16 h-16 rounded-2xl bg-amber-500/[0.06] border border-amber-500/20 mx-auto mb-4 flex items-center justify-center">
            <Bookmark className="w-7 h-7 text-amber-300" />
          </div>
          <p className="text-white/60 text-sm font-medium">No saved roles yet</p>
          <p className="text-white/30 text-xs mt-1 max-w-sm mx-auto">Tap the bookmark icon on any vacancy to save it here — we'll email your inbox before it closes.</p>
          <Button onClick={() => navigate('/vacancies')} className="mt-4 bg-white text-black hover:bg-white/90 rounded-xl text-xs">
            Explore vacancies
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map(v => {
            const closingDate = v.closing_date ? new Date(v.closing_date) : null;
            const daysLeft = closingDate ? Math.ceil((closingDate - new Date()) / 86400000) : null;
            const closingSoon = daysLeft !== null && daysLeft >= 0 && daysLeft <= 3;
            const closed = daysLeft !== null && daysLeft < 0;
            return (
              <div key={v.id} data-testid={`saved-role-${v.id}`}
                className={`rounded-2xl border p-5 transition-all group ${
                  closed
                    ? 'bg-white/[0.01] border-white/[0.04] opacity-60'
                    : closingSoon
                      ? 'bg-amber-500/[0.03] border-amber-500/25'
                      : 'bg-white/[0.02] border-white/[0.06] hover:border-[#00E5FF]/20'
                }`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1.5">
                      <h3 className="text-white text-base font-medium truncate">{v.title}</h3>
                      {v.is_urgent && (
                        <span className="px-2 py-0.5 rounded-md bg-red-500/10 text-red-300 border border-red-500/25 text-[9px] tracking-[0.08em] uppercase font-medium inline-flex items-center gap-1">
                          <Zap className="w-2.5 h-2.5" /> Urgent
                        </span>
                      )}
                      {closingSoon && !closed && (
                        <span className="px-2 py-0.5 rounded-md bg-amber-500/15 text-amber-300 border border-amber-500/30 text-[9px] tracking-[0.08em] uppercase font-medium inline-flex items-center gap-1" data-testid="closing-soon-badge">
                          <AlertTriangle className="w-2.5 h-2.5" /> Closing in {daysLeft} day{daysLeft === 1 ? '' : 's'}
                        </span>
                      )}
                      {closed && (
                        <span className="px-2 py-0.5 rounded-md bg-white/[0.04] text-white/40 border border-white/[0.08] text-[9px] tracking-[0.08em] uppercase font-medium">
                          Closed
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-[11px] text-white/40 flex-wrap">
                      {v.department && <span className="text-[#c39be5]">{v.department}</span>}
                      {v.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{v.location}</span>}
                      {v.type && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{v.type}</span>}
                      {closingDate && !closed && !closingSoon && (
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" />Closes {closingDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => unsave(v.id)}
                      disabled={removing === v.id}
                      data-testid={`unsave-role-${v.id}`}
                      title="Remove from saved"
                      className="w-9 h-9 rounded-full bg-white/[0.03] border border-white/[0.06] text-amber-300 hover:bg-white/[0.06] hover:border-amber-500/25 flex items-center justify-center transition-all disabled:opacity-50">
                      <BookmarkCheck className="w-4 h-4" />
                    </button>
                    <Button
                      onClick={() => navigate('/vacancies')}
                      disabled={closed}
                      data-testid={`view-role-${v.id}`}
                      className={`h-9 px-4 rounded-full text-[10px] tracking-[0.12em] uppercase font-semibold ${
                        closed
                          ? 'bg-white/[0.03] text-white/30 border border-white/[0.06] cursor-not-allowed'
                          : 'bg-gradient-to-br from-[#00E5FF] to-[#0099CC] text-black hover:opacity-90'
                      }`}>
                      View <ExternalLink className="w-3 h-3 ml-1.5" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SavedRolesTab;
